import { formatLineDimensions } from '../../utils/productDimensions';
import { sanitizeProductDisplayName } from '../../utils/productDisplayNameSanitizer';
import { getProductVariantAttributeEntries } from '../../utils/productSelectorLabel';

type DimensionValue = number | string | null | undefined;

export interface OrderProductDisplayInput {
  name?: string | null;
  productVariantId?: string | null;
  productCode?: string | null;
  productEan?: string | null;
  variantSku?: string | null;
  catalogProductName?: string | null;
  variantName?: string | null;
  variantAttributes?: Record<string, string>;
  variantIsDefault?: boolean;
  width?: DimensionValue;
  height?: DimensionValue;
  length?: DimensionValue;
  thickness?: DimensionValue;
  catalogProductWidth?: DimensionValue;
  catalogProductHeight?: DimensionValue;
  catalogProductLength?: DimensionValue;
  catalogProductThickness?: DimensionValue;
  productCategoryDimensionDisplayProfile?: string | null;
}

const getOrderProductTechnicalValues = (product: OrderProductDisplayInput) => [
  product.productVariantId,
  product.productCode,
  product.productEan,
  product.variantSku,
];

/**
 * Linked order rows can retain pre-catalog names containing codes, UUIDs and
 * duplicated dimensions. Preserve the historical order-line name by default
 * because list/search consumers rely on it to distinguish variants.
 */
export const getOrderProductDisplayName = (
  product?: OrderProductDisplayInput | null,
  fallback = '',
) => {
  if (!product) return fallback;

  const technicalValues = getOrderProductTechnicalValues(product);

  for (const candidate of [product.name, product.catalogProductName]) {
    const displayName = sanitizeProductDisplayName(candidate, technicalValues);
    if (displayName) return displayName;
  }

  return fallback;
};

/**
 * The detail table renders variant labels separately, so it can prefer the
 * clean catalog product name without collapsing variant identity elsewhere.
 */
export const getOrderProductDetailDisplayName = (
  product?: OrderProductDisplayInput | null,
  fallback = '',
) => {
  if (!product) return fallback;

  const hasCatalogVariantPresentation = product.variantIsDefault !== undefined
    || product.variantAttributes !== undefined;
  if (hasCatalogVariantPresentation) {
    const catalogName = sanitizeProductDisplayName(
      product.catalogProductName,
      getOrderProductTechnicalValues(product),
    );
    if (catalogName) return catalogName;
  }

  return getOrderProductDisplayName(product, fallback);
};

export interface OrderProductVariantLabel {
  key?: string;
  value: string;
  isBaseProduct?: boolean;
}

/**
 * Order detail mirrors the selector's current catalog labels without parsing
 * the persisted historical line name. If the linked catalog row no longer
 * exists, the order keeps its stored name and simply has no variant labels.
 */
export const getOrderProductVariantLabels = (
  product: OrderProductDisplayInput | null | undefined,
  basicProductLabel: string,
): OrderProductVariantLabel[] => {
  if (!product?.productVariantId) return [];

  if (product.variantIsDefault === true) {
    return [{ value: basicProductLabel, isBaseProduct: true }];
  }

  const attributeEntries = getProductVariantAttributeEntries({
    attributes: product.variantAttributes || {},
  });
  if (attributeEntries.length > 0) {
    return attributeEntries.map(([key, value]) => ({ key, value }));
  }

  if (product.variantIsDefault === false) {
    const fallback = sanitizeProductDisplayName(
      product.variantName,
      getOrderProductTechnicalValues(product),
    );
    if (fallback && fallback !== 'Standard') return [{ value: fallback }];
  }

  return [];
};

/**
 * Order-line dimensions are the historical snapshot and always win. Missing
 * axes fall back independently to the linked catalog product so legacy rows
 * can follow today's category display profile without rewriting history.
 */
export const getOrderProductDisplayDimensions = (
  product?: OrderProductDisplayInput | null,
) => formatLineDimensions(product);

/**
 * „Nazwa (gr. 12 x szer. 1250 x dł. 3000 mm)” for prose (guide rows, hints)
 * where a separate dimensions line does not fit; the bare name when the
 * line has no dimensions.
 */
export const withLineDimensions = (
  name: string,
  product?: OrderProductDisplayInput | null,
) => {
  const dimensions = getOrderProductDisplayDimensions(product);
  return dimensions ? `${name} (${dimensions})` : name;
};
